export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

export const today = '2026-07-14';

const TOKEN_KEY = 'creatorcv_token';

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

export function authHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function authRequest(path, body) {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload?.error?.message || 'Request failed');
  }
  if (payload.token) setToken(payload.token);
  return payload;
}

export async function joinMailingList(email, country, attribution = {}) {
  const response = await fetch(`${API_BASE_URL}/v1/mailing-list`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, country, ...attribution }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const fieldMessage = payload?.error?.fields && Object.values(payload.error.fields)[0];
    throw new Error(fieldMessage || payload?.error?.message || 'Could not join the mailing list');
  }
  return payload;
}

export async function uploadImage(file) {
  const formData = new FormData();
  formData.append('image', file);
  const response = await fetch(`${API_BASE_URL}/v1/uploads/image`, {
    method: 'POST',
    headers: authHeaders(),
    body: formData,
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload?.error?.message || 'Image upload failed');
  }
  return payload;
}

export async function checkUsernameAvailable(username) {
  const response = await fetch(`${API_BASE_URL}/v1/profiles/check-username?username=${encodeURIComponent(username)}`, {
    headers: { Accept: 'application/json', ...authHeaders() },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload?.error?.message || 'Request failed');
  }
  return payload.available;
}

export async function fetchPublicProfile(username) {
  const response = await fetch(`${API_BASE_URL}/v1/profiles/by-username/${encodeURIComponent(username)}`, {
    headers: { Accept: 'application/json' },
  });
  if (response.status === 404) return null;
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload?.error?.message || 'Request failed');
  }
  return payload.profile;
}

export async function authGet(path) {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    headers: { Accept: 'application/json', ...authHeaders() },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload?.error?.message || 'Request failed');
  }
  return payload;
}

export async function fetchMyBasics() {
  const payload = await authGet('/v1/profiles/me/basics');
  return payload.profile;
}

export async function fetchMySocials() {
  const payload = await authGet('/v1/profiles/me/socials');
  return payload.socials;
}

export async function fetchMyPortfolio() {
  const payload = await authGet('/v1/profiles/me/portfolio');
  return payload.portfolio;
}

export async function fetchMyCollaborations() {
  const payload = await authGet('/v1/profiles/me/collaborations');
  return payload.collaborations;
}

export function defaultCreatorInfo() {
  return {
    displayName: '',
    username: '',
    headline: '',
    bio: '',
    location: '',
    languages: [],
    creatorTypes: [],
    niches: [],
    opportunityPreferences: [],
    collaborationWishlistBrands: [],
    collaborationWishlistCategories: [],
    creatorGoals: [],
    contactEmail: '',
    contactInfo: '',
    availability: 'Available for collaborations',
    visibility: 'Private',
    published: false,
    avatarUrl: '',
    coverUrl: '',
  };
}

// The backend always returns a real (zero-value) creator, never null, so a
// brand-new account (no displayName/username/headline/bio yet) needs the
// opinionated placeholder fields applied client-side instead of relying on
// response truthiness.
export function withCreatorDefaults(creator) {
  const looksUnset = !creator.displayName && !creator.username && !creator.headline && !creator.bio;
  const defaults = defaultCreatorInfo();
  return {
    ...creator,
    languages: creator.languages || [],
    creatorTypes: creator.creatorTypes || [],
    niches: creator.niches || [],
    opportunityPreferences: creator.opportunityPreferences || [],
    collaborationWishlistBrands: creator.collaborationWishlistBrands || [],
    collaborationWishlistCategories: creator.collaborationWishlistCategories || [],
    creatorGoals: creator.creatorGoals || [],
    location: creator.location || defaults.location,
    availability: looksUnset ? creator.availability || defaults.availability : creator.availability,
    visibility: looksUnset ? creator.visibility || defaults.visibility : creator.visibility,
  };
}
